import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getExcelTemplate, getPdfTemplate } from '../templates/export/excelConfig';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function DiseñadorPlantillas() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [templateName, setTemplateName] = useState('');
  const [baseTemplate, setBaseTemplate] = useState('standard');
  const [exportType, setExportType] = useState('excel');

  // Estructura de la plantilla
  const [templateStructure, setTemplateStructure] = useState({
    excel: {
      secciones: [
        {
          id: 'encabezado',
          nombre: 'Encabezado',
          activo: true,
          orden: 1,
          elementos: [
            { tipo: 'titulo', texto: 'PRESUPUESTO DE OBRA', estilo: 'h1' },
            { tipo: 'empresa', mostrar: true },
            { tipo: 'datos_obra', campos: ['cliente', 'obra', 'ubicacion', 'fecha'] }
          ]
        },
        {
          id: 'tabla',
          nombre: 'Tabla de Conceptos',
          activo: true,
          orden: 2,
          columnas: [
            { campo: 'partida', titulo: 'Partida', visible: false, ancho: 'auto' },
            { campo: 'codigo', titulo: 'Código', visible: true, ancho: 'auto' },
            { campo: 'descripcion', titulo: 'Descripción', visible: true, ancho: '40%' },
            { campo: 'unidad', titulo: 'Unidad', visible: true, ancho: 'auto' },
            { campo: 'cantidad', titulo: 'Cantidad', visible: true, ancho: 'auto' },
            { campo: 'precio_unitario', titulo: 'P.U.', visible: true, ancho: 'auto' },
            { campo: 'importe', titulo: 'Importe', visible: true, ancho: 'auto' }
          ]
        },
        {
          id: 'totales',
          nombre: 'Totales',
          activo: true,
          orden: 3,
          campos: [
            { campo: 'subtotal', etiqueta: 'Subtotal', mostrar: true },
            { campo: 'indirectos', etiqueta: 'Indirectos', mostrar: true, porcentaje: true },
            { campo: 'financiamiento', etiqueta: 'Financiamiento', mostrar: true, porcentaje: true },
            { campo: 'utilidad', etiqueta: 'Utilidad', mostrar: true, porcentaje: true },
            { campo: 'iva', etiqueta: 'IVA', mostrar: true, porcentaje: true },
            { campo: 'total', etiqueta: 'TOTAL', mostrar: true, resaltado: true }
          ]
        },
        {
          id: 'pie',
          nombre: 'Pie de Página',
          activo: false,
          orden: 4,
          elementos: [
            { tipo: 'texto', contenido: 'Presupuesto válido por 30 días' },
            { tipo: 'firmas', mostrar: false }
          ]
        }
      ]
    },
    pdf: {
      secciones: [
        {
          id: 'encabezado',
          nombre: 'Encabezado',
          activo: true,
          orden: 1,
          elementos: [
            { tipo: 'logo', posicion: 'centro', mostrar: false },
            { tipo: 'empresa', estilo: 'titulo' },
            { tipo: 'contacto', mostrar: true },
            { tipo: 'titulo', texto: 'PRESUPUESTO DE OBRA', estilo: 'h1' },
            { tipo: 'datos_obra', disposicion: 'horizontal' }
          ]
        },
        {
          id: 'tabla',
          nombre: 'Tabla de Conceptos',
          activo: true,
          orden: 2,
          estilo: 'grid',
          encabezadoColor: '#1976d2',
          alternarFilas: true
        },
        {
          id: 'totales',
          nombre: 'Totales',
          activo: true,
          orden: 3,
          alineacion: 'derecha',
          mostrarTexto: true
        },
        {
          id: 'pie',
          nombre: 'Pie de Página',
          activo: true,
          orden: 4,
          elementos: [
            { tipo: 'numeracion', formato: 'Página {n} de {total}' },
            { tipo: 'fecha_impresion', mostrar: true },
            { tipo: 'notas', texto: '' }
          ]
        }
      ]
    }
  });

  const [selectedSection, setSelectedSection] = useState(null);
  const [customTemplates, setCustomTemplates] = useState({});

  useEffect(() => {
    // Cargar plantillas personalizadas guardadas
    const saved = localStorage.getItem('custom_template_designs');
    if (saved) {
      setCustomTemplates(JSON.parse(saved));
    }
  }, []);

  const handleSaveTemplate = () => {
    if (!templateName) {
      toast.error('Por favor ingresa un nombre para la plantilla');
      return;
    }

    const newTemplate = {
      name: templateName,
      baseTemplate,
      exportType,
      structure: templateStructure,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString()
    };

    const updatedTemplates = {
      ...customTemplates,
      [templateName]: newTemplate
    };

    localStorage.setItem('custom_template_designs', JSON.stringify(updatedTemplates));
    setCustomTemplates(updatedTemplates);
    toast.success('Plantilla guardada exitosamente');
  };

  const loadTemplate = (name) => {
    const template = customTemplates[name];
    if (template) {
      setTemplateName(name);
      setBaseTemplate(template.baseTemplate);
      setExportType(template.exportType);
      setTemplateStructure(template.structure);
      toast.success(`Plantilla "${name}" cargada`);
    }
  };

  const deleteTemplate = (name) => {
    const updated = { ...customTemplates };
    delete updated[name];
    localStorage.setItem('custom_template_designs', JSON.stringify(updated));
    setCustomTemplates(updated);
    toast.success('Plantilla eliminada');
  };

  const moveSection = (index, direction) => {
    const sections = [...templateStructure[exportType].secciones];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < sections.length) {
      [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
      sections[index].orden = index + 1;
      sections[newIndex].orden = newIndex + 1;
      setTemplateStructure({
        ...templateStructure,
        [exportType]: { ...templateStructure[exportType], secciones: sections }
      });
    }
  };

  const toggleSection = (sectionId) => {
    const sections = templateStructure[exportType].secciones.map(s =>
      s.id === sectionId ? { ...s, activo: !s.activo } : s
    );
    setTemplateStructure({
      ...templateStructure,
      [exportType]: { ...templateStructure[exportType], secciones: sections }
    });
  };

  const updateColumn = (columnIndex, field, value) => {
    const sections = [...templateStructure[exportType].secciones];
    const tableSection = sections.find(s => s.id === 'tabla');
    if (tableSection && tableSection.columnas) {
      tableSection.columnas[columnIndex][field] = value;
      setTemplateStructure({
        ...templateStructure,
        [exportType]: { ...templateStructure[exportType], secciones: sections }
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Diseñador de Plantillas
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Diseña la estructura y disposición de los elementos en tus plantillas de exportación.
        Los datos de empresa y configuración general se aplican a todas las plantillas.
      </Alert>

      <Grid container spacing={3}>
        {/* Panel Izquierdo - Lista de Plantillas */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Plantillas Guardadas
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ mb: 2 }}
              onClick={() => {
                setTemplateName('');
                setTemplateStructure({
                  excel: { ...templateStructure.excel },
                  pdf: { ...templateStructure.pdf }
                });
              }}
            >
              Nueva Plantilla
            </Button>

            <List>
              {Object.entries(customTemplates).map(([name, template]) => (
                <ListItem key={name} button onClick={() => loadTemplate(name)}>
                  <ListItemText
                    primary={name}
                    secondary={
                      <Stack direction="row" spacing={1}>
                        <Chip size="small" label={template.exportType} />
                        <Typography variant="caption">
                          {new Date(template.modifiedAt).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => deleteTemplate(name)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Panel Central - Editor */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nombre de la Plantilla"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Mi Plantilla Custom"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Plantilla Base</InputLabel>
                  <Select
                    value={baseTemplate}
                    onChange={(e) => setBaseTemplate(e.target.value)}
                    label="Plantilla Base"
                  >
                    <MenuItem value="standard">Estándar</MenuItem>
                    <MenuItem value="detallada">Detallada</MenuItem>
                    <MenuItem value="simplificada">Simplificada</MenuItem>
                    <MenuItem value="corporativa">Corporativa</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Exportación</InputLabel>
                  <Select
                    value={exportType}
                    onChange={(e) => setExportType(e.target.value)}
                    label="Tipo de Exportación"
                  >
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="pdf">PDF</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Estructura de la Plantilla
            </Typography>

            {templateStructure[exportType].secciones.map((section, index) => (
              <Accordion key={section.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={section.activo}
                          onChange={() => toggleSection(section.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={section.nombre}
                      sx={{ flexGrow: 1 }}
                    />
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(index, -1);
                        }}
                        disabled={index === 0}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(index, 1);
                        }}
                        disabled={index === templateStructure[exportType].secciones.length - 1}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {section.id === 'tabla' && section.columnas && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Columnas de la Tabla
                      </Typography>
                      {section.columnas.map((col, colIndex) => (
                        <Box key={col.campo} sx={{ mb: 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={col.visible}
                                onChange={(e) => updateColumn(colIndex, 'visible', e.target.checked)}
                              />
                            }
                            label={col.titulo}
                          />
                          {col.visible && (
                            <TextField
                              size="small"
                              label="Ancho"
                              value={col.ancho}
                              onChange={(e) => updateColumn(colIndex, 'ancho', e.target.value)}
                              sx={{ ml: 2, width: 100 }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {section.id === 'totales' && section.campos && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Campos de Totales
                      </Typography>
                      {section.campos.map((campo, fieldIndex) => (
                        <Box key={campo.campo} sx={{ mb: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={campo.mostrar}
                                onChange={(e) => {
                                  const sections = [...templateStructure[exportType].secciones];
                                  const totalesSection = sections.find(s => s.id === 'totales');
                                  totalesSection.campos[fieldIndex].mostrar = e.target.checked;
                                  setTemplateStructure({
                                    ...templateStructure,
                                    [exportType]: { ...templateStructure[exportType], secciones: sections }
                                  });
                                }}
                              />
                            }
                            label={`${campo.etiqueta} ${campo.porcentaje ? '(%)' : ''}`}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {section.elementos && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Elementos
                      </Typography>
                      {section.elementos.map((elemento, elemIndex) => (
                        <Box key={elemIndex} sx={{ ml: 2, mb: 1 }}>
                          <Typography variant="body2">
                            • {elemento.tipo === 'titulo' ? elemento.texto : elemento.tipo}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>

        {/* Panel Derecho - Vista Previa */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Vista Previa
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              Vista previa simplificada. La exportación real puede variar.
            </Alert>

            <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
              {templateStructure[exportType].secciones
                .filter(s => s.activo)
                .map(section => (
                  <Box key={section.id} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="primary">
                      {section.nombre}
                    </Typography>
                    {section.id === 'tabla' && (
                      <Box sx={{ fontSize: '10px', mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, fontWeight: 'bold' }}>
                          {section.columnas?.filter(c => c.visible).map(c => (
                            <Box key={c.campo} sx={{ flex: c.ancho === '40%' ? 2 : 1 }}>
                              {c.titulo}
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ borderTop: '1px solid #ddd', mt: 1, pt: 1 }}>
                          [Datos de ejemplo]
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<PreviewIcon />}
              sx={{ mt: 2 }}
              onClick={() => toast('Vista previa completa próximamente', { icon: 'ℹ️' })}
            >
              Previsualizar Completo
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Botones de Acción */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/configuracion-plantillas')}
        >
          Volver a Configuración
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => {
              const newName = `${templateName} (copia)`;
              setTemplateName(newName);
              toast('Plantilla duplicada, cambia el nombre para guardar', { icon: 'ℹ️' });
            }}
          >
            Duplicar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveTemplate}
          >
            Guardar Plantilla
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default DiseñadorPlantillas;